import React, { createContext, useContext, useState, useCallback, useRef } from "react";

interface SyncContextType {
  triggerLeadsRefresh: () => void;
  triggerAppointmentsRefresh: () => void;
  onLeadsRefresh: (callback: () => void) => () => void;
  onAppointmentsRefresh: (callback: () => void) => () => void;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const leadsRefreshCallbacks = useRef<Set<() => void>>(new Set());
  const appointmentsRefreshCallbacks = useRef<Set<() => void>>(new Set());

  const triggerLeadsRefresh = useCallback(() => {
    leadsRefreshCallbacks.current.forEach(callback => callback());
  }, []);

  const triggerAppointmentsRefresh = useCallback(() => {
    appointmentsRefreshCallbacks.current.forEach(callback => callback());
  }, []);

  const onLeadsRefresh = useCallback((callback: () => void) => {
    leadsRefreshCallbacks.current.add(callback);
    return () => {
      leadsRefreshCallbacks.current.delete(callback);
    };
  }, []);

  const onAppointmentsRefresh = useCallback((callback: () => void) => {
    appointmentsRefreshCallbacks.current.add(callback);
    return () => {
      appointmentsRefreshCallbacks.current.delete(callback);
    };
  }, []);

  return (
    <SyncContext.Provider value={{
      triggerLeadsRefresh,
      triggerAppointmentsRefresh,
      onLeadsRefresh,
      onAppointmentsRefresh
    }}>
      {children}
    </SyncContext.Provider>
  );
}

export const useSync = () => {
  const context = useContext(SyncContext);
  if (context === undefined) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};
